import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Dialog } from "@/components/ui";
import { HoloShimmer } from "@/components/Holo";
import { cn } from "@/lib/utils";
import { DesignPackageDetail } from "@/services/bedrock/dtos/DesignPackage";
import { writeEditorParams } from "../editorSearchParams";
import { baseThemeOption } from "../baseTheme";
import { EditorThemeOption, useEditorThemes } from "../hooks/useEditorThemes";
import { usePreviews } from "../hooks/usePreviews";
import { ASPECT_CLASS } from "./PackageCard";
import { ThemeSwatches } from "./ThemeSwitcher";

interface AllThemesGridProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pkg: DesignPackageDetail;
  selectedItemId: number;
  /** The theme the editor shows now; its tile is marked Current. */
  currentThemeId?: number | null;
}

function useVisible<T extends Element>(): [React.RefCallback<T>, boolean] {
  const [node, setNode] = React.useState<T | null>(null);
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    if (!node || visible) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) setVisible(true);
      },
      { threshold: 0.25 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [node, visible]);

  return [React.useCallback((element: T | null) => setNode(element), []), visible];
}

const ThemeCard: React.FC<{
  pkg: DesignPackageDetail;
  selectedItemId: number;
  option: EditorThemeOption;
  /** The preview to show: the theme's own, or null for the base preview the default theme stands in for. */
  themeId: number | null;
  current: boolean;
  onSelect: () => void;
}> = ({ pkg, selectedItemId, option, themeId, current, onSelect }) => {
  const [ref, visible] = useVisible<HTMLDivElement>();
  const { set, slideFor, ensureRendered, renderState, retry } = usePreviews(pkg.id, themeId);
  const slide = slideFor(selectedItemId);
  const slideStates = set?.slides.map(s => s.status).join(",");

  React.useEffect(() => {
    if (visible) ensureRendered();
  }, [visible, slideStates, ensureRendered]);

  const failed = renderState === "failed" && !slide.url;
  const idle = !slide.url && renderState !== "rendering" && !!set && !failed;
  // Failed and idle tiles hold their own button, so only the others can be one button as a whole.
  const selectable = !failed && !idle;

  const image = () => {
    if (failed) {
      return (
        <div className="flex size-full flex-col items-center justify-center gap-2 p-4 text-center">
          <span className="text-xs text-slate-500">We couldn't render this preview.</span>
          <Button variant="ghost" size="sm" onClick={retry}>
            Try again
          </Button>
        </div>
      );
    }
    if (slide.url) return <img src={slide.url} alt={option.theme.name} className="size-full object-cover" />;
    if (!idle) return <HoloShimmer aspectRatio={pkg.aspectRatio} className="rounded-none" />;
    return (
      <div className="flex size-full items-center justify-center">
        <Button variant="ghost" size="sm" onClick={retry}>
          Show preview
        </Button>
      </div>
    );
  };

  const body = (
    <>
      <div className={cn("relative w-full overflow-hidden bg-slate-50", ASPECT_CLASS[pkg.aspectRatio])}>
        {image()}
        {selectable && !current && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 bg-slate-900/60 py-1.5 text-center text-xs font-semibold text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
          >
            Preview in editor
          </span>
        )}
      </div>
      <div className="flex w-full min-w-0 items-center gap-2 border-t border-slate-200 bg-white px-3 py-2">
        <ThemeSwatches theme={option.theme} />
        <span className="truncate text-left text-sm font-medium text-slate-700" title={option.label}>
          {option.label}
        </span>
        {current && (
          <span className="ml-auto shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
            Current
          </span>
        )}
      </div>
    </>
  );

  const tileClass = cn(
    "group flex w-full flex-col overflow-hidden rounded-xl border bg-white text-left",
    current ? "border-primary ring-2 ring-primary/20" : "border-slate-200",
    selectable &&
      !current &&
      "cursor-pointer transition-all hover:-translate-y-px hover:border-slate-300 hover:shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
  );

  return (
    <div ref={ref} data-testid="theme-card" data-theme-id={option.theme.id} data-current={current}>
      {selectable ? (
        <button
          type="button"
          onClick={onSelect}
          className={tileClass}
          aria-label={current ? `${option.label}, current theme` : `Preview ${option.label} in the editor`}
        >
          {body}
        </button>
      ) : (
        <div className={tileClass}>{body}</div>
      )}
    </div>
  );
};

export const AllThemesGrid: React.FC<AllThemesGridProps> = ({
  open,
  onOpenChange,
  pkg,
  selectedItemId,
  currentThemeId = null,
}) => {
  const history = useHistory();
  const location = useLocation();
  const { options } = useEditorThemes();
  const base = baseThemeOption(options, pkg.coachId);
  // The default theme is the base preview, which the editor shows without a theme id.
  const themeIdFor = (option: EditorThemeOption) => (option === base ? null : option.theme.id);

  const select = (themeId: number | null) => {
    writeEditorParams(history, location, { theme: themeId });
    onOpenChange(false);
  };

  const position = pkg.items.find(item => item.id === selectedItemId)?.position;
  const subject = pkg.items.length > 1 && position != null ? `Slide ${position}` : "Your design";

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="3xl">
      <Dialog.Header>
        <Dialog.Title className="text-xl font-bold font-sans">Compare themes</Dialog.Title>
        <Dialog.Description className="text-sm text-slate-500">
          {subject} in each of your themes, using sample data. Pick one to preview it in the editor.
        </Dialog.Description>
      </Dialog.Header>
      <Dialog.Content className="max-h-[70vh] overflow-y-auto p-6">
        <div
          className={cn(
            "grid gap-x-6 gap-y-8",
            pkg.aspectRatio === "portrait_9_16" ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2 md:grid-cols-3"
          )}
        >
          {options.map(option => (
            <ThemeCard
              key={option.theme.id}
              pkg={pkg}
              selectedItemId={selectedItemId}
              option={option}
              themeId={themeIdFor(option)}
              current={themeIdFor(option) === currentThemeId || option.theme.id === currentThemeId}
              onSelect={() => select(themeIdFor(option))}
            />
          ))}
        </div>
      </Dialog.Content>
    </Dialog>
  );
};
